import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
// material
import { Stack, Button, Typography, Box, Grid } from '@mui/material';
import Iconify from '../../components/Iconify';
import BackDropLoader from '../../components/BackDropLoader';
import { useDisclosureWithOpts } from '../../hooks/useDisclosure';
import { deleteTraining, getTrainings } from '../../features/trainings/services/trainingsService';
import { useLocalization } from '../../contexts/localizationContext';
import TrainingDialog from '../../features/trainings/components/TrainingDialog';
import UsersOnTrainingDialog from '../../features/trainings/components/UsersOnTrainingDialog';
import CopyTrainings from '../../features/trainings/components/CopyTrainingsDialog';
import Table from '../../components/Table/Table';
import { useAuth } from '../../contexts/authContext';
import { useTheme  } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { TrainingBlock } from '../../features/trainings/components/TrainingBlock';

export default function TrainingsPage() {

  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [trainingsData, setTrainingsData] = useState({ isFetching: true, trainings: [] });
  const { enqueueSnackbar } = useSnackbar();
  const dialogDisclosure = useDisclosureWithOpts();
  const usersOnTrainingDialogDisclosure  = useDisclosureWithOpts();
  const localization = useLocalization();
  const auth = useAuth();
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = useCallback(async (showSuccesMsg = true, showErrorMsg = true) => {
    try {
      const trainings = await getTrainings(auth.user.id);
      if (showSuccesMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.success' defaultMessage='Trainings successfully fetched' />, {
        variant: 'success',
      });
      setTrainingsData({ isFetching: false, trainings });
    } catch (error) {
      if (showErrorMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.error' defaultMessage='Trainings fetch failed' />, {
        variant: 'error',
      });
      setTrainingsData({ isFetching: false, trainings: [] });
    }
  }, [setTrainingsData]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (row) => {
    setLoading(true);
    try {
      await deleteTraining(row.id);
      await fetchData(false);
      enqueueSnackbar(<FormattedMessage id='trainings.delete.msg.success' defaultMessage='Trainings successfully deleted' />, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='trainings.delete.msg.error' defaultMessage='Trainings delete failed' />, {
        variant: 'error',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (rowData) => {
    dialogDisclosure.open({ id: rowData.id });
  };

  const handleButtonCreateTraining = () => {
    dialogDisclosure.open();
  };

  const handleDialogClose = (refetchData) => {
    if (refetchData) {
      fetchData(false);
    }
    dialogDisclosure.close();
  };

  const handleUsersByTrainingDialogClose = () => {
    usersOnTrainingDialogDisclosure.close();
  };

  const handleOpenModal = async (trainingId) => {
    setSelectedTrainingId(trainingId);
    usersOnTrainingDialogDisclosure.open();
  };

  const handleCopyTrainingsClose = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {isSmallScreen ? (
        //mobile screen part
        <div>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <BackDropLoader open={loading} />
            <Grid container spacing={2} justifyContent="center" alignItems="center" flexGrow={1}>
              <Grid item xs={12}>
                <CopyTrainings onClose={handleCopyTrainingsClose} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={handleButtonCreateTraining}
                  style={{ width: '100%' }}
                >
                  <FormattedMessage id='common.button.createTraining' defaultMessage='Create training' />
                </Button>
              </Grid>
            </Grid>
          </Box>
          <br/>
          <TrainingBlock handleOpenModal={handleOpenModal} handleDelete={handleDelete} handleRowClick={handleRowClick} trainingsData={trainingsData} />

          {usersOnTrainingDialogDisclosure.isOpen && (
            <UsersOnTrainingDialog
              open={usersOnTrainingDialogDisclosure.isOpen}
              onClose={handleUsersByTrainingDialogClose}
              trainingId={selectedTrainingId}
            />
          )}
          {dialogDisclosure.isOpen && (
            <TrainingDialog
              open={dialogDisclosure.isOpen}
              id={dialogDisclosure.opts.id}
              onClose={handleDialogClose}
            />
          )}
        </div>
      ) : (
        //desktop part
        <div>
          <BackDropLoader open={loading} />
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" mb={5}>
            <CopyTrainings onClose={handleCopyTrainingsClose} />
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleButtonCreateTraining}
            >
              <FormattedMessage id='common.button.createTraining' defaultMessage='Create training' />
            </Button>
          </Stack>
          <Table
            columns={[
              { title: intl.formatMessage({ id: 'training.form.label.name', defaultMessage: 'Name' }), field: 'name' },
              { title: intl.formatMessage({ id: 'training.form.label.availableSpots', defaultMessage: 'Available Spots' }), field: 'availableSpots', type: 'numeric' },
              { title: intl.formatMessage({ id: 'training.form.label.reservationTimeFrom', defaultMessage: 'Reservation Time From' }), field: 'reservationTimeFrom', type: 'datetime', dateSetting: { locale: localization.locale } },
              { title: intl.formatMessage({ id: 'training.form.label.reservationTimeTo', defaultMessage: 'Reservation Time To' }), field: 'reservationTimeTo', type: 'datetime', dateSetting: { locale: localization.locale } },
              { title: intl.formatMessage({ id: 'training.form.label.location', defaultMessage: 'Location' }), field: 'location' },
            ]}
            data={trainingsData.trainings}
            onRowClick={(event, rowData) => handleRowClick(rowData)}
            actions={[
              {
                tooltip: intl.formatMessage({ id: 'trainings.tooltip.whoIsOnTraining', defaultMessage: 'Signed up for training'}),
                icon: InfoIcon,
                onClick: (evt, data) => handleOpenModal(data.id),
                position: 'row'
              },
              {
                tooltip: intl.formatMessage({ id: 'trainings.tooltip.removeTraining', defaultMessage: 'Remove Training'}),
                icon: DeleteIcon,
                onClick: (evt, data) => handleDelete(data),
                position: 'row'
              }
            ]}
            isLoading={trainingsData.isFetching}
            detailPanel={({ rowData }) => {
              return (
                <Box
                  style={{
                    height: 100,
                    padding: 20
                  }}
                >
                  <Typography>{rowData.description}</Typography>
                </Box>
              );
            }}
          />
          {usersOnTrainingDialogDisclosure.isOpen && (
            <UsersOnTrainingDialog
              open={usersOnTrainingDialogDisclosure.isOpen}
              onClose={handleUsersByTrainingDialogClose}
              trainingId={selectedTrainingId}
            />
          )}
          {dialogDisclosure.isOpen && (
            <TrainingDialog
              open={dialogDisclosure.isOpen}
              id={dialogDisclosure.opts.id}
              onClose={handleDialogClose}
            />
          )}
        </div>
      )}
    </>
  );
}